/**
 * HeroSection — the most complex editor.  Two modes:
 *
 *  Slider mode (default):
 *    - 4 sub-panels, one per slide
 *    - Each slide: gradient picker + image URL + eyebrow/title/description + CTA
 *
 *  Video mode:
 *    - One sub-panel
 *    - Video URL + poster URL (these live in settings, not content)
 *    - Single eyebrow/title/description/CTA pair (reuses path hero.video.{...})
 *
 * Mode is driven by `settings.hero_mode` (from useLandingDraft) and the
 * settings update is instant (no Publish gating) so editors can preview
 * the toggle live.
 */

import {
  BilingualTextField, BilingualTextareaField,
  SectionPanel, GradientPicker,
} from '../fields/BilingualFields'
import FileUploadField from '../fields/FileUploadField'

function SlideEditor({ index, getField, setField }) {
  const base = `hero.slides.${index}`
  const enGradient = getField('en', `${base}.gradient`, '')

  return (
    <div className="bg-gray-50 rounded-lg p-4 border border-gray-200 space-y-2">
      <h4 className="text-sm font-semibold text-gray-700 mb-2">Slide {index + 1}</h4>

      <GradientPicker
        label="Background gradient (visual)"
        value={enGradient}
        onChange={(val) => {
          // Same gradient for both langs (it's a visual decision).
          setField('en', `${base}.gradient`, val)
          setField('ar', `${base}.gradient`, val)
        }}
      />

      <FileUploadField
        label="Slide image (shared across languages)"
        kind="image"
        value={getField('en', `${base}.imageUrl`, '')}
        onChange={(val) => {
          setField('en', `${base}.imageUrl`, val)
          setField('ar', `${base}.imageUrl`, val)
        }}
        hint="Optional — overrides the bundled hero image."
      />

      <BilingualTextField
        label="Eyebrow (small text above title)"
        path={`${base}.subtitle`}
        getField={getField} setField={setField}
      />
      <BilingualTextField
        label="Title"
        path={`${base}.title`}
        getField={getField} setField={setField}
      />
      <BilingualTextareaField
        label="Description"
        path={`${base}.description`}
        getField={getField} setField={setField}
        rows={3}
      />
    </div>
  )
}

function VideoEditor({ getField, setField, settings, updateSettings, savingSettings }) {
  return (
    <div className="space-y-3">
      <FileUploadField
        label="Background video (MP4 / WebM / MOV)"
        kind="video"
        value={settings.hero_video_url}
        onChange={(val) => updateSettings({ hero_video_url: val })}
        hint="Muted autoplay loop. Recommend ≤ 5 MB for fast mobile loading."
      />
      <FileUploadField
        label="Video poster image (shown while video loads)"
        kind="image"
        value={settings.hero_video_poster_url}
        onChange={(val) => updateSettings({ hero_video_poster_url: val })}
      />
      {savingSettings && (
        <p className="text-xs text-gray-500">Saving settings…</p>
      )}

      <div className="bg-gray-50 rounded-lg p-4 border border-gray-200 mt-4">
        <h4 className="text-sm font-semibold text-gray-700 mb-2">Overlay text</h4>
        <BilingualTextField
          label="Eyebrow"
          path="hero.video.subtitle"
          getField={getField} setField={setField}
        />
        <BilingualTextField
          label="Title"
          path="hero.video.title"
          getField={getField} setField={setField}
        />
        <BilingualTextareaField
          label="Description"
          path="hero.video.description"
          getField={getField} setField={setField}
          rows={3}
        />
      </div>
    </div>
  )
}

export default function HeroSection({ getField, setField, settings, updateSettings, savingSettings }) {
  const mode = settings.hero_mode || 'slider'

  return (
    <SectionPanel title="Hero — top of page" subtitle="Switch between sliding banners and full-bleed video">
      {/* Mode picker — settings, not content */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
        <div className="text-xs font-semibold text-blue-900 mb-2 uppercase tracking-wide">Mode</div>
        <div className="inline-flex bg-white border border-blue-300 rounded-lg overflow-hidden">
          {['slider', 'video'].map((opt) => (
            <button
              key={opt}
              type="button"
              onClick={() => updateSettings({ hero_mode: opt })}
              className={`px-4 py-2 text-sm font-medium transition-colors ${
                mode === opt
                  ? 'bg-blue-600 text-white'
                  : 'bg-white text-blue-700 hover:bg-blue-50'
              }`}
            >
              {opt === 'slider' ? 'Slider (4 banners)' : 'Video background'}
            </button>
          ))}
        </div>
        <p className="text-xs text-blue-700/80 mt-2">
          Switching modes applies instantly to the public site — no Publish required.
        </p>
      </div>

      {/* Shared: CTA button label across modes */}
      <BilingualTextField
        label="CTA button label (Get Started)"
        path="hero.ctaGetStarted"
        getField={getField} setField={setField}
      />

      <hr className="my-4 border-gray-200" />

      {mode === 'slider' ? (
        <div className="space-y-4">
          {[0, 1, 2, 3].map((i) => (
            <SlideEditor key={i} index={i} getField={getField} setField={setField} />
          ))}
        </div>
      ) : (
        <VideoEditor
          getField={getField} setField={setField}
          settings={settings}
          updateSettings={updateSettings}
          savingSettings={savingSettings}
        />
      )}
    </SectionPanel>
  )
}
